import { PopoverWidget, TabStyles } from '@tableau/widgets';
import React, { ComponentType, CSSProperties } from 'react';

import { sanitize } from '~commonInfra/utils/Sanitize';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { CardResizerService } from '~src/spacesPlatform/services/CardResizerService';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Formatters } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { DraftsById } from '../../../drafts/types/DraftsTypes';
import { getContentSpaceDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { isRecommendationType, isSpaceInfoItemType } from '../../models/ContentType';
import { IContentInfo } from '../../models/IContentInfo';
import { IFavoritableItemInfo } from '../../models/resourceInfoParts';
import { TextileHelper } from '../../utils/TextileHelper';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { DetailButton } from '../header/DetailButton';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { ContentTooltipProps } from '../tooltip/TooltipHelper';
import { CARD_POPOVER_WIDTH } from './CardViewStyles';

export interface ContentCardProps extends SiteUrlsProps {
  readonly content: IContentInfo;
  readonly selected: boolean;
  readonly hovered?: boolean;
  readonly forDragPreview: boolean;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly getContentDetail: (content: IContentInfo) => void;
  readonly inWorkbookDatasourcesSubspace: boolean;
  readonly inDatasourceConnectionsSubspace: boolean;
  readonly inProjectSpace?: boolean;
  readonly inRecentsSpace?: boolean;
  readonly inPersonalSpace?: boolean;
  readonly allowDragAndDrop: boolean;
  readonly showActionMenu: boolean;
  readonly showLastAccessedAt?: boolean;
  readonly drafts?: DraftsById;
  readonly spaceInfo?: SpaceInfo;
  readonly inRecycleBinSpace?: boolean;
}

export interface ContentCardState {
  cardZoomPercentage: number;
}

export const maxDescriptionLengthWithDraft = 48;
export const getCardDetailButtonTestId = (content: IContentInfo): string =>
  `${content.contentType}-${content.id}-popover-trigger`;

export class ContentCard<Props extends ContentCardProps = ContentCardProps, State extends ContentCardState = ContentCardState>
  extends React.PureComponent<Props, ContentCardState> {

  private removeCardResizeListener: () => void;

  constructor(props: Props) {
    super(props);
    this.state = {
      cardZoomPercentage: CardResizerService.instance.getCardZoomPercentage()
    };
  }

  protected getDescription() {
    return TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
  }

  protected shouldRenderExtraInformationAtBottom() {
    return this.state.cardZoomPercentage >= 0.9;
  }

  protected getDescriptionWithDraft() {
    let descriptionPlaintext = TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
    return L10n.elideIfLong(descriptionPlaintext, maxDescriptionLengthWithDraft);
  }

  protected getLastAccessedAt() {
    return this.props.content.lastAccessedAt ? Formatters.formatLongBestFitRelativeTime(new Date(this.props.content.lastAccessedAt)) : '';
  }

  protected renderActionsMenu(): JSX.Element {
    return this.props.showActionMenu &&
      <ActionsMenu
        targetItems={[this.props.content]}
        disabled={this.props.selected}
        useIcon={true}
        sourceComponent='Card' />;
  }

  protected getPopoverComponent(): ComponentType<ContentTooltipProps> {
    // This method is intended to be overridden in child classes
    return () => null;
  }

  protected overridePopoverProps(): Partial<ContentTooltipProps> | null {
    return null;
  }

  protected renderPopoverContent(): JSX.Element {
    const Component = this.getPopoverComponent();
    const tooltipProps: ContentTooltipProps = {
      content: this.props.content,
      contentUrl: getContentUrlForItem(this.props.content, this.props.urls),
      urls: this.props.urls,
      inProjectSpace: this.props.inProjectSpace,
      inPersonalSpace: this.props.inPersonalSpace,

      ...this.overridePopoverProps()
    };
    return <Component {...tooltipProps} />;
  }

  protected handleShowPopover = () => {
    this.props.getContentDetail(this.props.content);
  };

  protected getCardZoomStyle(): CSSProperties {
    return {
      zoom: this.state.cardZoomPercentage
    };
  }

  protected getCardZoomStyleForThumbnailText(): CSSProperties {
    return {
      zoom: 1 / this.state.cardZoomPercentage
    };
  }

  protected getCardZoomStyleForActionRow(): CSSProperties {
    return {
      marginTop: TabStyles.Spacing.MediumSpace * this.getCardZoomForText()
    };
  }

  protected getCardZoomStyleForText(): CSSProperties {
    return {
      zoom: this.getCardZoomForText()
    };
  }

  private getCardZoomForText(): number {
    return this.state.cardZoomPercentage < 1 ? this.state.cardZoomPercentage : 1;
  }

  private cardResizeListener = (newCardZoomPercentage: number) => {
    this.setState({
      cardZoomPercentage: newCardZoomPercentage
    });
  };

  public componentDidMount() {
    this.removeCardResizeListener = CardResizerService.instance.registerListener(this.cardResizeListener);
  }

  public componentWillUnmount() {
    if (this.removeCardResizeListener) {
      this.removeCardResizeListener();
    }
  }
}

export function getContentUrlForItem(content: IContentInfo, urls: SpacesSiteUrls): string | undefined {
  let contentType = content.contentType;
  if (isRecommendationType(contentType)) {
    contentType = 'view';
  }

  // it only makes sense to generate a url for items that have their own space
  // and hence are SpaceInfoItemType('s)
  if (isSpaceInfoItemType(contentType)) {
    const contentDefinition = getContentSpaceDefinition(contentType);

    return contentDefinition.contentSpaceUrl(urls, content.id, content.luid);
  }
}

export interface CardPopoverWidgetProps {
  /** The content rendered inside of the popover. */
  popoverContent: JSX.Element;
  /** Forwarded to popover `triggerElement` */
  onTriggerClick: () => void;
  /** Title for the popover, needed for accessibility  */
  title: string;
  /** The content item for which the detail will be shown */
  content: IContentInfo;
  /** If provided, forwarded to `aria-labelledby` on the popover `triggerElement` */
  buttonLabelId?: string;
}

export const CardPopoverWidget: React.FC<CardPopoverWidgetProps> = (props) => {
  return <PopoverWidget
    popoverContent={props.popoverContent}
    title={props.title}
    triggerElement={<DetailButton testId={getCardDetailButtonTestId(props.content)} onClick={props.onTriggerClick} aria-labelledby={props.buttonLabelId} />}
    width={CARD_POPOVER_WIDTH}
  />;
};
